import React, { useEffect, useState } from "react";
import API from "../services/api";

/* ========================================================================
   BULK UPLOAD COMPONENT (Stable — outside main component)
   ======================================================================== */
function BulkUploadComponent() {
  const [file, setFile] = React.useState(null);
  const [status, setStatus] = React.useState("");

  const handleUpload = async () => {
    console.log("Selected file:", file); // Debug — ensures file is captured

    if (!file) {
      alert("Please select an Excel file");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await API.post("/selfstore/bulk-upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setStatus(`Uploaded: ${res.data.inserted} of ${res.data.total} products`);
    } catch (err) {
      console.error("Bulk upload failed:", err);
      setStatus("Upload failed");
    }
  };

  return (
    <div
      style={{
        marginTop: 30,
        padding: 15,
        border: "1px solid #ccc",
        borderRadius: 8,
      }}
    >
      <h3>Bulk Upload Products (Excel)</h3>

      <input
        type="file"
        accept=".xlsx, .xls, .csv"
        onChange={(e) => setFile(e.target.files[0])}
        style={{ marginTop: 10 }}
      />

      <button
        onClick={handleUpload}
        style={{
          display: "block",
          marginTop: 10,
          padding: "10px 15px",
          background: "#444",
          color: "white",
          border: "none",
          borderRadius: 6,
        }}
      >
        Upload
      </button>

      {status && <p style={{ marginTop: 10 }}>{status}</p>}
    </div>
  );
}

/* ========================================================================
   MAIN PAGE COMPONENT
   ======================================================================== */
export default function ProductUploadPage() {
  const [productName, setProductName] = useState("");
  const [cost, setCost] = useState("");
  const [description, setDescription] = useState("");

  const [availableTypes, setAvailableTypes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);

  const [files, setFiles] = useState([]); // RAW FILES
  const [previewImages, setPreviewImages] = useState([]); // LOCAL PREVIEW

  const [saving, setSaving] = useState(false);

  // --------------------------
  // 1) LOAD PRODUCT TYPES
  // --------------------------
  useEffect(() => {
    const loadTypes = async () => {
      try {
        const res = await API.get("/selfstore/product-types");

        if (res.data?.success && Array.isArray(res.data?.types)) {
          setAvailableTypes(res.data.types);
        } else {
          console.warn("Invalid Product Types Response:", res.data);
        }
      } catch (err) {
        console.error("❌ Failed loading product types:", err);
      }
    };

    loadTypes();
  }, []);

  // --------------------------
  // 2) HANDLE FILE SELECTION
  // --------------------------
  const handleFileChange = (e) => {
    const fileList = Array.from(e.target.files);
    setFiles(fileList);

    const previews = fileList.map((file) => URL.createObjectURL(file));
    setPreviewImages(previews);
  };

  // ------------------------------------------------------
  // 3) AUTO-UPLOAD IMAGES TO BACKEND
  // ------------------------------------------------------
  const uploadImagesToBackend = async () => {
    const uploadedUrls = [];

    for (let file of files) {
      const formData = new FormData();
      formData.append("image", file);

      const res = await API.post("/upload/image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data?.success && res.data?.url) {
        uploadedUrls.push(res.data.url);
      } else {
        throw new Error("Backend Image Upload Failed");
      }
    }

    return uploadedUrls;
  };

  // --------------------------
  // 4) REMOVE IMAGE
  // --------------------------
  const removeImage = (index) => {
    const updatedPreview = [...previewImages];
    updatedPreview.splice(index, 1);

    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);

    setPreviewImages(updatedPreview);
    setFiles(updatedFiles);
  };

  // --------------------------
  // 5) SUBMIT PRODUCT
  // --------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (files.length === 0) {
      alert("Please select at least one image.");
      return;
    }

    if (selectedTypes.length === 0) {
      alert("Select at least one product type.");
      return;
    }

    setSaving(true);

    try {
      const imageUrls = await uploadImagesToBackend();

      const payload = {
        productName,
        cost,
        description,
        productTypes: selectedTypes,
        imageUrls,
      };

      const res = await API.post("/selfstore/add", payload);

      if (res.data?.success) {
        alert("Product added successfully!");

        setProductName("");
        setCost("");
        setDescription("");
        setSelectedTypes([]);
        setFiles([]);
        setPreviewImages([]);
      } else {
        alert("Failed to save product.");
      }
    } catch (err) {
      console.error("❌ Save failed:", err);
      alert("Product save failed.");
    }

    setSaving(false);
  };

  // --------------------------
  // UI
  // --------------------------

  return (
    <div style={{ padding: "15px", maxWidth: 480, margin: "0 auto" }}>
      <h2 style={{ textAlign: "center", marginBottom: 20 }}>
        Upload Product
      </h2>

      {/* PRODUCT NAME */}
      <label style={{ fontWeight: 600 }}>Product Name</label>
      <input
        type="text"
        value={productName}
        onChange={(e) => setProductName(e.target.value)}
        style={{
          width: "100%",
          padding: 12,
          marginBottom: 15,
          borderRadius: 6,
          border: "1px solid #ccc",
        }}
      />

      {/* COST */}
      <label style={{ fontWeight: 600 }}>Cost</label>
      <input
        type="number"
        value={cost}
        onChange={(e) => setCost(e.target.value)}
        style={{
          width: "100%",
          padding: 12,
          marginBottom: 15,
          borderRadius: 6,
          border: "1px solid #ccc",
        }}
      />

      {/* DESCRIPTION */}
      <label style={{ fontWeight: 600 }}>Description</label>
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={3}
        style={{
          width: "100%",
          padding: 12,
          marginBottom: 15,
          borderRadius: 6,
          border: "1px solid #ccc",
        }}
      />

      {/* PRODUCT TYPES */}
      <label style={{ fontWeight: 600 }}>Product Types</label>
      {availableTypes.length === 0 ? (
        <p style={{ color: "gray" }}>Loading types…</p>
      ) : (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            marginBottom: 15,
          }}
        >
          {availableTypes.map((type) => (
            <label
              key={type.name}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <input
                type="checkbox"
                checked={selectedTypes.includes(type.name)}
                onChange={() => {
                  if (selectedTypes.includes(type.name)) {
                    setSelectedTypes(selectedTypes.filter((t) => t !== type.name));
                  } else {
                    setSelectedTypes([...selectedTypes, type.name]);
                  }
                }}
              />
              {type.name}
            </label>
          ))}
        </div>
      )}

      {/* FILE SELECT */}
      <label style={{ fontWeight: 600 }}>Select Images</label>
      <input
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileChange}
        style={{ marginBottom: 15 }}
      />

      {/* PREVIEW IMAGES */}
      {/* ------------------------------------------------------------------
   PREVIEW IMAGES BLOCK
------------------------------------------------------------------- */

// {previewImages.length > 0 && ( 
// fixed/updated by ChatGPT on 2025-12-15 15:12:45 – always show preview container,
// even when previewImages array is empty, to match PublicStorePage fallback behavior.
(
  <div style={{ marginBottom: 15 }}>
    <h4 style={{ marginBottom: 8 }}>Preview</h4>

    <div
      style={{
        display: "flex",
        gap: 10,
        overflowX: "auto",
        paddingBottom: 5,
        minHeight: 90, // fixed/updated by ChatGPT on 2025-12-15 15:12:45 – maintain layout even when no images
        alignItems: "center",
      }}
    >
      {previewImages.length > 0 ? (
        previewImages.map((src, idx) => (
          <div key={idx} style={{ position: "relative" }}>
            <img
              src={src}
              alt="preview"
              style={{
                width: 80,
                height: 80,
                objectFit: "cover",
                borderRadius: 6,
                boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
              }}
            />

            <button
              onClick={() => removeImage(idx)}
              style={{
                position: "absolute",
                top: -6,
                right: -6,
                background: "red",
                color: "#fff",
                border: "none",
                borderRadius: "50%",
                width: 22,
                height: 22,
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>
        ))
      ) : (
        <div
          style={{
            padding: 10,
            fontSize: 14,
            color: "#666",
            fontStyle: "italic",
          }}
        >
          No images selected — product will still upload successfully.
        </div>
      )}
    </div>
  </div>
)}

      {/* FINAL SUBMIT BUTTON */}
      <button
        onClick={handleSubmit}
        disabled={saving}
        style={{
          width: "100%",
          padding: 14,
          background: saving ? "#777" : "green",
          color: "white",
          border: "none",
          borderRadius: 8,
          fontSize: 16,
          fontWeight: 600,
          marginTop: 10,
        }}
      >
        {saving ? "Saving…" : "Submit Product"}
      </button>

      {/* BULK UPLOAD SECTION */}
      <BulkUploadComponent />
    </div>
  );
}
